/** Redeeming an invitation, from the browser's side — **ADR-0046**.
 *
 * Two calls, both **unauthenticated**: nobody has a session yet, and redeeming is the act that
 * makes a person a member. Neither is a query. They are `useMutation`s with `gcTime: 0`, for the
 * reason `useEnrolMfa` gives: a mutation's variables stay in React Query's mutation cache for five
 * minutes by default, and these variables are a **bearer token and a password**. There is
 * deliberately nothing here to cache, key or refetch.
 *
 * **Every way a claim can fail is one answer.** The API returns the same 404 and the same body for
 * an unknown token, a wrong address, an expired claim, a used one and a revoked one — status *and*
 * body, asserted by `test_claims_api.py` — because any distinction is an oracle: "wrong address"
 * confirms the token is real, "already used" confirms somebody joined. So this module returns
 * `refused` and nothing finer, and **no field of the response body is carried out of it**. It is
 * not that the body is secret; it is that a client which reads a reason today renders a reason the
 * day somebody adds one.
 *
 * What is *not* a refusal stays distinguishable, because it is a different fact with a different
 * remedy: a request that never completed, a proxy's 404, a 5xx. Collapsing those into "this link
 * cannot be used" would tell somebody to ask for a new invitation because our API was down.
 */

import { useMutation } from "@tanstack/react-query";

import { api } from "./client";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

export type ClaimRole = components["schemas"]["ClaimPreviewOut"]["role"];

/** What a correct token-and-address pair may be told, and no more (ADR-0046). */
export interface ClaimPreview {
  organizationName: string;
  role: ClaimRole;
  /** `true` for a new person, who sets a password at redemption; `false` for an existing one,
   * whose password is left exactly as it is. */
  needsCredential: boolean;
}

export type PreviewResult = ({ status: "previewed" } & ClaimPreview) | { status: "refused" };

export type RedeemResult =
  | { status: "redeemed"; organizationName: string }
  | { status: "refused" }
  /** **The password was too short for the server's policy.** A distinguishable outcome rather than
   * part of `refused`, for the reason this module's header gives: a refusal and a thing with a
   * different remedy are different facts, and collapsing them tells somebody to ask for a new
   * invitation when all they need is a longer password.
   *
   * It carries **no message**, because this module carries no field of a response body out of
   * itself. The sentence is the screen's, built from `MIN_PASSWORD_LENGTH`. */
  | { status: "too_weak" }
  /** **The request was malformed, so the server refused to look at it** — any other 422. Validation
   * runs before the route body, so nothing was redeemed and the token is not spent. Distinct from
   * an interrupted request on purpose: reporting this as "we could not confirm whether you joined"
   * sends the person to sign in, which cannot work because they did not join. It carries no
   * message, for the reason `too_weak` does not. */
  | { status: "invalid" };

export interface ClaimLookup {
  token: string;
  email: string;
}

export interface ClaimRedemption extends ClaimLookup {
  displayName: string;
  /** Present for a new person. **Absent — not empty — for an existing one**, because the server
   * ignores it there as a security property and a client that sent one would be offering a
   * credential it has no business holding. */
  password?: string;
}

/** The API's one claim refusal: a 404 *carrying its own code*.
 *
 * Both halves matter. A bare 404 is also what a mis-set API origin or a proxy answers, and calling
 * that "your invitation is no good" would blame the person for a deployment fault.
 */
function isClaimRefusal(response: Response, code: string): boolean {
  return response.status === 404 && code === "claim_refused";
}

export function usePreviewClaim() {
  return useMutation({
    gcTime: 0,
    mutationFn: async (input: ClaimLookup): Promise<PreviewResult> => {
      const { data, error, response } = await api.POST("/api/v1/auth/claims/preview", {
        body: { token: input.token, email: input.email },
      });
      if (data) {
        return {
          status: "previewed",
          organizationName: data.organization_name,
          role: data.role,
          needsCredential: data.needs_credential,
        };
      }
      const failure = refusalFrom(response, error, "The invitation could not be checked.");
      if (isClaimRefusal(response, failure.code)) return { status: "refused" };
      throw failure;
    },
  });
}

export function useRedeemClaim() {
  return useMutation({
    gcTime: 0,
    mutationFn: async (input: ClaimRedemption): Promise<RedeemResult> => {
      const { data, error, response } = await api.POST("/api/v1/auth/claims/redeem", {
        body: {
          token: input.token,
          email: input.email,
          display_name: input.displayName,
          // Spread, not `password: undefined`: JSON drops an undefined, but the **absence of the
          // key** is the property under test and should not rest on that.
          ...(input.password === undefined ? {} : { password: input.password }),
        },
      });
      if (data) return { status: "redeemed", organizationName: data.organization_name };
      const failure = refusalFrom(response, error, "The invitation could not be redeemed.");
      if (isClaimRefusal(response, failure.code)) return { status: "refused" };
      // **Reachable only if the client's minimum and the server's have drifted apart**, because
      // the screen checks the length before spending a single-use token. It is handled anyway: the
      // alternative is what happened on 2026-10-16, when the server answered 500 for a short
      // password and this `throw` became "we could not confirm whether you joined" — advice to go
      // and sign in, which was the one thing that could not work.
      if (response.status === 422 && failure.code === "password_too_weak") {
        return { status: "too_weak" };
      }
      if (response.status === 422) return { status: "invalid" };
      throw failure;
    },
  });
}
